import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext/AuthContext';
import {
  Box, Card, CardContent, CardMedia, Typography, IconButton,
  Button, Tooltip, Chip, CircularProgress, Tabs, Tab,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import {
  FavoriteBorder, Favorite, Share, Call, Chat,
  LocationOn, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import axios from 'axios';
import buildingImage from './../../../Images/house.jpeg';
import CustomSearchBar from '../Lease Module/CustomSearch';
import BottomNavbar from './CustomNavbarHotProperties';
import { BASE_URL } from '../../Api/ApiUrls';
import { NODE_API } from '../Buy Module/NodeApi';
import LimitModal from '../Buy Module/LimitModal';
import SubscriptionModal from '../Buy Module/SubscriptionModal';
import AlertModal from '../Buy Module/AlertModal';
import GuestLoginModal from '../../GuestLoginModal';
import CopyFailureAlert from '../Alertpage/CopyAlert/CopyFailureAlert';
import CopySuccessAlert from '../Alertpage/CopyAlert/CopySuccessAlert';

const PAGE_SIZE = 20;

// ─── Skeleton card ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <Box sx={{
    mb: 1.2, mx: 2, borderRadius: 3, overflow: 'hidden', boxShadow: 2,
    backgroundColor: '#fff',
    animation: 'skeletonPulse 1.5s ease-in-out infinite',
    '@keyframes skeletonPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
  }}>
    <Box sx={{ height: 140, backgroundColor: '#ddd' }} />
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ height: 14, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '65%' }} />
      <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '45%' }} />
      <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1.5, width: '55%' }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        {[1, 2, 3].map(i => (
          <Box key={i} sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
        ))}
      </Box>
    </Box>
  </Box>
);

// ─── Chip skeleton row ──────────────────────────────────────────────────────────
const ChipRowSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 1, overflowX: 'hidden' }}>
    {[80, 60, 100, 70, 90].map((w, i) => (
      <Box key={i} sx={{
        width: w, height: 32, borderRadius: 16, flexShrink: 0,
        backgroundColor: '#d0d0d0',
        animation: 'skeletonPulse 1.5s ease-in-out infinite',
        '@keyframes skeletonPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
      }} />
    ))}
  </Box>
);

// ─── Scroll sentinel ────────────────────────────────────────────────────────────
const ScrollSentinel = ({ onVisible, hasMore, loading }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) onVisible(); },
      { threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible, hasMore, loading]);

  return (
    <Box ref={ref} sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {loading && <CircularProgress size={28} sx={{ color: '#8B7355' }} />}
      {!loading && !hasMore && (
        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
          ✓ All properties loaded
        </Typography>
      )}
    </Box>
  );
};

// ─── Parse raw API row → card shape ────────────────────────────────────────────
const parseProperty = (item) => {
  const parseCoord = (coord) => {
    if (!coord) return null;
    return parseFloat(String(coord).replace(/"/g, '').replace(':', '.'));
  };

  let images = [buildingImage];
  if (item.property_images?.length > 0) {
    images = item.property_images.map(img => {
      if (typeof img === 'string') return img.startsWith('http') ? img : `${BASE_URL}${img}`;
      if (img.image) return img.image.startsWith('http') ? img.image : `${BASE_URL}/${img.image.replace(/^\/+/, '')}`;
      if (img.url)   return img.url.startsWith('http')   ? img.url   : `${BASE_URL}${img.url}`;
      if (img.path)  return img.path.startsWith('http')  ? img.path  : `${BASE_URL}${img.path}`;
      return buildingImage;
    });
  } else if (item.images?.length > 0) {
    images = item.images.map(img =>
      typeof img === 'string' && img.startsWith('http') ? img : `${BASE_URL}${img}`
    );
  }

  return {
    id:            String(item.property_id),
    title:         item.category_name || item.property_type || 'Property',
    property_name: item.property_name,
    location:      item.location || 'Not specified',
    date:          item.created_at?.split('T')[0],
    facing:        item.facing?.replace(/"/g, '') || 'N/A',
    price:         Number(item.price) || 0,
    length:        item.length,
    width:         item.width,
    mobile_no:     item.mobile_no,
    units:         item.units || '',
    category_id:   item.category_id,
    category_name: item.category_name || '',
    images,
    site_area: item.site_area,
    area: item.site_area
      ? `${item.site_area} ${['ft','m','cm','in'].includes(item.units) ? `Sq ${item.units}` : item.units || 'Sq ft'}`
      : 0,
    posted_by:    item.posted_by?.replace(/"/g, '') || 'Agent',
    lat:          parseCoord(item.lat),
    long:         parseCoord(item.long),
    owner:        item.user_id,
    propertyData: item,
    property_images: item.property_images,
    property_type:   item.property_type || '',
  };
};

// ─── Debounce hook ──────────────────────────────────────────────────────────────
const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// ─── Generic paginated fetch hook ──────────────────────────────────────────────
const usePaginatedFetch = (getUrl, deps, enabled = true) => {
  const [items,          setItems]          = useState([]);
  const [cursor,         setCursor]         = useState(null);
  const [hasMore,        setHasMore]        = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const fetchingRef = useRef(false);
  const [isFetching, setIsFetching] = useState(false);
  const abortRef = useRef(null);

  const setFetching = (val) => { fetchingRef.current = val; setIsFetching(val); };

  useEffect(() => {
    if (!enabled) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setItems([]); setCursor(null); setHasMore(false);
    setInitialLoading(true); setFetching(true);

    fetch(getUrl(null), { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(json => {
        setItems((json.data || []).map(parseProperty));
        setCursor(json.next_cursor ?? null);
        setHasMore(json.has_more ?? false);
      })
      .catch(err => { if (err.name !== 'AbortError') console.error('fetch error:', err); })
      .finally(() => { setInitialLoading(false); setFetching(false); });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore || !cursor) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);

    fetch(getUrl(cursor), { signal: ctrl.signal })
      .then(r => r.json())
      .then(json => {
        setItems(prev => [...prev, ...(json.data || []).map(parseProperty)]);
        setCursor(json.next_cursor ?? null);
        setHasMore(json.has_more ?? false);
      })
      .catch(err => { if (err.name !== 'AbortError') console.error('loadMore error:', err); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, cursor, ...deps]);

  return { items, hasMore, loading, initialLoading, isFetching, fetchingRef, loadMore };
};

// ─── Hook: all hot/best-deal properties (no category filter) ───────────────────
const useAllHotProperties = ({ searchQuery, priceFilter, enabled }) => {
  const getUrl = useCallback((cursorVal) => {
    const p = new URLSearchParams({ limit: PAGE_SIZE });
    if (cursorVal)                p.set('cursor',    cursorVal);
    if (searchQuery.trim())       p.set('search',    searchQuery.trim());
    if (priceFilter?.min != null) p.set('min_price', priceFilter.min);
    if (priceFilter?.max != null) p.set('max_price', priceFilter.max);
    return `${NODE_API}/property-request/hot/non-admin/?${p}`;
  }, [searchQuery, priceFilter]);

  return usePaginatedFetch(
    getUrl,
    [searchQuery, JSON.stringify(priceFilter)],
    enabled
  );
};

// ─── Hook: hot properties filtered by category_id ──────────────────────────────
const useHotByCategory = ({ selectedCategoryId, searchQuery, priceFilter, enabled }) => {
  const getUrl = useCallback((cursorVal) => {
    const p = new URLSearchParams({ limit: PAGE_SIZE });
    if (cursorVal)                p.set('cursor',    cursorVal);
    if (searchQuery?.trim())      p.set('search',    searchQuery.trim());
    if (priceFilter?.min != null) p.set('min_price', priceFilter.min);
    if (priceFilter?.max != null) p.set('max_price', priceFilter.max);
    return `${NODE_API}/property-request/hot/by-category/${selectedCategoryId}?${p}`;
  }, [selectedCategoryId, searchQuery, priceFilter]);

  return usePaginatedFetch(
    getUrl,
    [selectedCategoryId, searchQuery, JSON.stringify(priceFilter)],
    enabled && selectedCategoryId != null
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────────
const PropertyCard = () => {
  const navigate            = useNavigate();
  const { userId, isGuest } = useContext(AuthContext);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [activeTab,         setActiveTab]         = useState('buy');
  const [searchQuery,       setSearchQuery]       = useState('');
  const [selectedCategory,  setSelectedCategory]  = useState(null);
  const [priceFilter,       setPriceFilter]       = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const debouncedSearch = useDebounce(searchQuery, 400);
  const isTyping = searchQuery !== debouncedSearch;

  // ── Data state ───────────────────────────────────────────────────────────────
  const [categories,        setCategories]        = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [allFetchedProperties, setAllFetchedProperties] = useState([]);

  // ── Auth / subscription ──────────────────────────────────────────────────────
  const [savedProperties, setSavedProperties] = useState([]);
  const [cartItems,       setCartItems]       = useState([]);
  const [saving,          setSaving]          = useState({});
  const [locationLoading, setLocationLoading] = useState({});
  const [isSubscribed,    setIsSubscribed]    = useState(false);
  const [userPlan,        setUserPlan]        = useState(null);
  const [userActivities,  setUserActivities]  = useState([]);

  // ── Modals ───────────────────────────────────────────────────────────────────
  const [showLimitModal,     setShowLimitModal]     = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [featureRequested,   setFeatureRequested]   = useState('');
  const [showAlertModal,     setShowAlertModal]     = useState(false);
  const [pendingAction,      setPendingAction]      = useState(null);
  const [currentPropertyId,  setCurrentPropertyId]  = useState(null);
  const [showGuestModal,     setShowGuestModal]     = useState(false);
  const [showSuccess,        setShowSuccess]        = useState(false);
  const [showFailure,        setShowFailure]        = useState(false);
  const [alertMessage,       setAlertMessage]       = useState('');

  // ── Two fetch hooks ──────────────────────────────────────────────────────────
  const allProps = useAllHotProperties({
    searchQuery: debouncedSearch,
    priceFilter,
    enabled: activeTab === 'buy' && !selectedCategory,
  });

  const categoryProps = useHotByCategory({
    selectedCategoryId: selectedCategory?.category_id ?? null,
    searchQuery: debouncedSearch,
    priceFilter,
    enabled: activeTab === 'buy' && !!selectedCategory,
  });

  const {
    items:          buyProperties,
    hasMore:        buyHasMore,
    loading:        buyListLoading,
    initialLoading: buyInitialLoading,
    isFetching:     buyIsFetching,
    fetchingRef:    buyFetchingRef,
    loadMore:       buyLoadMore,
  } = selectedCategory ? categoryProps : allProps;

  const showSkeleton = isTyping || buyIsFetching || buyFetchingRef?.current;

  // Accumulate all ever-fetched properties (needed for accessed tab)
  useEffect(() => {
    if (!buyProperties.length) return;
    setAllFetchedProperties(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const newOnes = buyProperties.filter(p => !existingIds.has(p.id));
      return newOnes.length ? [...prev, ...newOnes] : prev;
    });
  }, [buyProperties]);

  // ─── MEMOIZED VALUES ──────────────────────────────────────────────────────────
  const accessedProperties = useMemo(() => {
    return allFetchedProperties.filter(property =>
      userActivities.some(
        a => String(a.property_id) === String(property.id) && a.activity_as === 'Buyer'
      )
    );
  }, [allFetchedProperties, userActivities]);

  const properties = useMemo(() => {
    return activeTab === 'accessed' ? accessedProperties : buyProperties;
  }, [activeTab, accessedProperties, buyProperties]);

  const hasMore = useMemo(() => {
    return activeTab === 'accessed' ? false : buyHasMore;
  }, [activeTab, buyHasMore]);

  const listLoading = useMemo(() => {
    return activeTab === 'accessed' ? false : buyListLoading;
  }, [activeTab, buyListLoading]);

  const initialLoading = useMemo(() => {
    return activeTab === 'accessed' ? false : buyInitialLoading;
  }, [activeTab, buyInitialLoading]);

  const loadMore = useMemo(() => {
    return activeTab === 'accessed' ? () => {} : buyLoadMore;
  }, [activeTab, buyLoadMore]);

  // ── Bootstrap ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setCategoriesLoading(true);
    fetch(`${NODE_API}/property-request/sell/categories/`)
      .then(r => r.json())
      .then(data => { setCategories(data); setCategoriesLoading(false); })
      .catch(() => setCategoriesLoading(false));
  }, []);

  useEffect(() => {
    if (!userId) return;

    axios.get(`${BASE_URL}/Get-user-plan/${userId}/`)
      .then(r => {
        const subs     = r.data;
        const buyerSub = Array.isArray(subs) ? subs.find(s => s.user_type === 'Buyer') : null;
        setIsSubscribed(!!buyerSub);
        setUserPlan(buyerSub || null);
      })
      .catch(() => { setIsSubscribed(false); setUserPlan(null); });

    axios.get(`${BASE_URL}/get-user-cart/${userId}/`)
      .then(r => { setCartItems(r.data); setSavedProperties(r.data.map(i => String(i.property_id))); })
      .catch(e => console.error('Cart error:', e));

    fetchUserActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ─── FIX: Optimize image index update ────────────────────────────────────────
  useEffect(() => {
    setCurrentImageIndex(prev => {
      let changed = false;
      const next = { ...prev };
      properties.forEach(p => { 
        if (!(p.id in next)) {
          next[p.id] = 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [properties]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const requireLogin = useCallback((e, cb) => {
    e.stopPropagation();
    if (isGuest) { setShowGuestModal(true); return; }
    cb();
  }, [isGuest]);

  const fetchUserActivities = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${BASE_URL}/activities/`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const all = await res.json();
      setUserActivities(all.filter(a => String(a.user_id) === String(userId)));
    } catch (e) {
      console.error('Activities error:', e);
      setUserActivities([]);
    }
  }, [userId]);

  const isSaved = useCallback((property) => {
    return savedProperties.includes(property.id);
  }, [savedProperties]);

  const addToCart = useCallback(async (propertyId) => {
    const r = await axios.post(`${BASE_URL}/user-cart/`, { user_id: userId, property_id: propertyId });
    return r.data;
  }, [userId]);

  const removeFromCart = useCallback(async (cartId) => {
    return axios.delete(`${BASE_URL}/user-cart/${cartId}/`);
  }, []);

  const toggleSave = useCallback(async (property) => {
    const pid = property.id;
    setSaving(prev => ({ ...prev, [pid]: true }));
    try {
      if (isSaved(property)) {
        const item = cartItems.find(i => i.property_id === pid);
        await removeFromCart(item.cart_id);
        setSavedProperties(prev => prev.filter(id => id !== pid));
        setCartItems(prev => prev.filter(i => i.property_id !== pid));
      } else {
        const newItem = await addToCart(pid);
        setSavedProperties(prev => [...prev, pid]);
        setCartItems(prev => [...prev, newItem.data]);
      }
    } catch (e) { console.error('Save error:', e); }
    finally { setSaving(prev => ({ ...prev, [pid]: false })); }
  }, [isSaved, cartItems, addToCart, removeFromCart]);

  const findProperty = useCallback((propertyId) => {
    return properties.find(p => p.id === propertyId) ||
           allFetchedProperties.find(p => p.id === propertyId);
  }, [properties, allFetchedProperties]);

  const checkPropertyLimit = useCallback((propertyId) => {
    if (!isSubscribed) {
      setFeatureRequested('Property Access');
      setShowSubscribeModal(true);
      return false;
    }
    if (userPlan?.no_of_properties_unlimited) return true;

    const alreadyAccessed = userActivities.some(
      a => a.property_id === propertyId && a.activity_as === 'Buyer'
    );
    if (alreadyAccessed) return true;

    const uniqueProps = new Set(
      userActivities.filter(a => a.activity_as === 'Buyer').map(a => a.property_id)
    );
    if (uniqueProps.size >= (userPlan?.no_of_properties || 10)) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  }, [isSubscribed, userPlan, userActivities]);

  const logActivity = useCallback(async (propertyId, activityType) => {
    const exists = userActivities.find(
      a => a.property_id === propertyId && a.activity_as === 'Buyer'
    );
    if (exists) return false;

    const property = findProperty(propertyId);
    if (!property) return false;

    try {
      const res = await fetch(`${BASE_URL}/activities/`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id:       userId,
          property_id:   propertyId,
          activity_type: activityType,
          property_by:   property.owner,
          activity_as:   'Buyer',
        }),
      });
      if (!res.ok) throw new Error('Failed to log activity');
      return await res.json();
    } catch (e) { console.error('logActivity error:', e); return false; }
  }, [userId, userActivities, findProperty]);

  const handleAction = useCallback(async (e, propertyId, actionName, actionCallback, activityType) => {
    e.stopPropagation();
    if (!isSubscribed) { setFeatureRequested(actionName); setShowSubscribeModal(true); return; }

    const alreadyAccessed = userActivities.some(
      a => a.property_id === propertyId && a.activity_as === 'Buyer'
    );
    if (!alreadyAccessed) {
      const ok = checkPropertyLimit(propertyId);
      if (!ok) return;
    }

    setPendingAction(() => async () => {
      try {
        await actionCallback();
        if (activityType && !alreadyAccessed) {
          const result = await logActivity(propertyId, activityType);
          if (result) {
            setUserActivities(prev => [...prev, {
              user_id:       userId,
              property_id:   propertyId,
              activity_type: activityType,
              property_by:   findProperty(propertyId)?.owner,
              activity_as:   'Buyer',
              created_at:    new Date().toISOString(),
            }]);
          }
        }
      } catch (err) {
        console.error(`${actionName} error:`, err);
        alert(`Failed to complete ${actionName} action`);
      }
    });
    setCurrentPropertyId(propertyId);
    setShowAlertModal(true);
  }, [isSubscribed, userActivities, checkPropertyLimit, logActivity, userId, findProperty]);

  // ── Action handlers ──────────────────────────────────────────────────────────
  const handleCallClick = useCallback((e, property) => {
    return handleAction(e, property.id, 'Call', async () => {
      try {
        await navigator.clipboard.writeText(property.mobile_no);
        setAlertMessage(`Copied: ${property.mobile_no}`);
        setShowSuccess(true); setShowFailure(false);
      } catch {
        setAlertMessage('Failed to copy number');
        setShowFailure(true); setShowSuccess(false);
      }
    }, 'Call');
  }, [handleAction]);

  const handleMessageClick = useCallback((e, property) => {
    return handleAction(e, property.id, 'Message', async () => {
      navigate('/chat', { state: { property: property.id } });
    }, 'Call');
  }, [handleAction, navigate]);

  const handleShareClick = useCallback(async (e, property) => {
    e.stopPropagation();
    const url = `${window.location.origin}/hot-property-description/${property.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setAlertMessage('Property link copied to clipboard!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
        setAlertMessage('Property link copied to clipboard!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch {
        setAlertMessage('Failed to copy property link');
        setShowFailure(true);
        setTimeout(() => setShowFailure(false), 3000);
      }
    }
  }, []);

  const handleLocationClick = useCallback((e, property) => {
    return handleAction(e, property.id, 'Location', async () => {
      setLocationLoading(prev => ({ ...prev, [property.id]: true }));
      const fallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.location)}`;
      const open = (url) => {
        setLocationLoading(prev => ({ ...prev, [property.id]: false }));
        window.open(url, '_blank', 'noopener,noreferrer');
      };
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            const dest = property.lat && property.long
              ? `https://www.google.com/maps/dir/?api=1&origin=${coords.latitude},${coords.longitude}&destination=${property.lat},${property.long}&travelmode=driving`
              : fallback;
            open(dest);
          },
          () => open(fallback),
          { timeout: 10000 }
        );
      } else { open(fallback); }
    }, 'Location');
  }, [handleAction]);

  const handleWishlistClick = useCallback((e, property) => {
    e.stopPropagation();
    return handleAction(e, property.id, 'Liked', async () => { await toggleSave(property); }, 'Liked');
  }, [handleAction, toggleSave]);

  const handleNextImage = useCallback((propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const p = findProperty(propertyId);
      return { ...prev, [propertyId]: (prev[propertyId] + 1) % p.images.length };
    });
  }, [findProperty]);

  const handlePrevImage = useCallback((propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const p = findProperty(propertyId);
      return { ...prev, [propertyId]: (prev[propertyId] - 1 + p.images.length) % p.images.length };
    });
  }, [findProperty]);

  // ── Card renderer ────────────────────────────────────────────────────────────
  const renderCard = useCallback((property) => {
    const isAccessed = userActivities.some(
      a => String(a.property_id) === String(property.id) && a.activity_as === 'Buyer'
    );
    const imgIdx = currentImageIndex[property.id] || 0;

    return (
      <Card
        key={property.id}
        sx={{
          mb: 1.2, mx: 2, borderRadius: 3, boxShadow: 2,
          transition: 'transform 0.2s ease-in-out',
          '&:hover': { transform: 'scale(1.015)', boxShadow: 4 },
        }}
        onClick={async (e) => {
          if (e.target.closest('button') || e.target.closest('svg')) return;
          if (!isSubscribed) { setFeatureRequested('View Details'); setShowSubscribeModal(true); return; }

          const alreadyAccessed = userActivities.some(
            a => a.property_id === property.id && a.activity_as === 'Buyer'
          );
          if (!alreadyAccessed) {
            const ok = checkPropertyLimit(property.id);
            if (!ok) return;
            try { await logActivity(property.id, 'Description'); } catch {}
          }
          navigate(`/hot-property-description/${property.id}`, {
            state: { propertyId: property.id, property: property.propertyData },
          });
        }}
      >
        {/* Image carousel */}
        <Box sx={{ position: 'relative', width: '100%', height: 140 }}>
          <CardMedia
            component="img"
            image={property.images[imgIdx]}
            alt="Property"
            sx={{ width: '100%', height: 140, objectFit: 'cover',
              borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
          />

          {property.images.length > 1 && (
            <>
              <IconButton
                sx={{ position: 'absolute', top: '50%', left: 8, opacity: 0,
                  transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(2px)',
                  '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.5)' } }}
                onClick={e => handlePrevImage(property.id, e)}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton
                sx={{ position: 'absolute', top: '50%', right: 8, opacity: 0,
                  transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(2px)',
                  '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.5)' } }}
                onClick={e => handleNextImage(property.id, e)}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </>
          )}

          {property.images.length > 1 && (
            <Box sx={{ position: 'absolute', bottom: 10, left: '50%',
              transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
              {property.images.map((_, i) => (
                <Box key={i}
                  onClick={e => { e.stopPropagation();
                    setCurrentImageIndex(prev => ({ ...prev, [property.id]: i })); }}
                  sx={{ width: 8, height: 8, borderRadius: '50%', cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    backgroundColor: imgIdx === i ? '#1976d2' : '#ccc' }}
                />
              ))}
            </Box>
          )}

          {/* Wishlist + Share */}
          <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.8 }}>
            <Tooltip title="Add to Wishlist">
              <IconButton
                sx={{ bgcolor: 'white', boxShadow: 1, p: 0.8 }}
                onClick={e => requireLogin(e, () => handleWishlistClick(e, property))}
                disabled={saving[property.id]}
              >
                {isSaved(property) ? <Favorite color="error" /> : <FavoriteBorder />}
                {saving[property.id] && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Share Property">
              <IconButton
                sx={{ bgcolor: 'white', boxShadow: 1, p: 0.8 }}
                onClick={e => requireLogin(e, () => handleShareClick(e, property))}
              >
                <Share />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Accessed badge */}
          {isAccessed && (
            <Box sx={{ position: 'absolute', bottom: 6, right: 6 }}>
              <Tooltip title="Already Accessed">
                <IconButton sx={{ bgcolor: 'white', boxShadow: 1, p: 0.8, color: 'green' }}
                  onClick={e => e.stopPropagation()}>
                  <CheckCircle />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Content */}
        <CardContent sx={{ px: 2, py: 0.2, pb: '7px !important' }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap
              sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', pr: 1 }}>
              {property.title} - {property.property_name}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="primary" noWrap>
              ₹{Number(String(property.price).replace(/[^0-9]/g, '')).toLocaleString('en-IN')}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary" noWrap
              sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', pr: 1 }}>
              {property.location}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {new Date(property.date).toLocaleDateString('en-IN',
                { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" mb={1}>
            <IconButton
              onClick={e => requireLogin(e, () => handleLocationClick(e, property))}
              size="small" sx={{ p: 0.5 }}
              disabled={locationLoading[property.id]}
            >
              {locationLoading[property.id]
                ? <CircularProgress size={16} />
                : <>
                    <LocationOn fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="caption" color="text.primary" ml={0.5}>Location</Typography>
                  </>}
            </IconButton>

            <Tooltip title="Send Message">
              <IconButton size="small"
                onClick={e => requireLogin(e, () => handleMessageClick(e, property))}
                sx={{ p: 0, color: 'primary.main', ml: 1 }}>
                <Chat fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              size="small" variant="outlined" color="success"
              startIcon={<Call fontSize="small" />}
              sx={{ textTransform: 'none', px: 1, py: 0.2, fontSize: '0.7rem', minWidth: 'auto' }}
              onClick={e => requireLogin(e, () => handleCallClick(e, property))}
            >
              Call
            </Button>
          </Box>

          <Box sx={{ display: 'flex', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            {[
              { label: 'Facing', value: property.facing || 'N/A' },
              {
                label: 'Area',
                value: (
                  <Box>
                    <Box component="span">{property.area || 'N/A'}</Box>
                    {property.length && property.width && (
                      <Typography component="span" variant="caption" color="text.secondary"
                        sx={{ display: ['none', 'inline'], ml: 0.5 }}>
                        ({property.length} × {property.width})
                      </Typography>
                    )}
                  </Box>
                ),
              },
              { label: 'Posted By', value: property.posted_by || 'N/A' },
            ].map((item, index) => (
              <Box key={index} sx={{
                flex: 1, px: 0.5, py: 0.2, textAlign: 'center', minWidth: 0,
                borderRight: index < 2 ? '1px solid #e0e0e0' : 'none',
              }}>
                <Typography variant="caption" color="text.secondary" noWrap>{item.label}</Typography>
                <Typography variant="body2" fontWeight="bold" noWrap={index !== 1}
                  sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }, [
    userActivities, currentImageIndex, isSubscribed, checkPropertyLimit, 
    logActivity, navigate, requireLogin, handleWishlistClick, handleShareClick,
    handleLocationClick, handleMessageClick, handleCallClick, saving, 
    isSaved, handlePrevImage, handleNextImage
  ]);

  // ── List section ─────────────────────────────────────────────────────────────
  const renderPropertiesSection = useMemo(() => {
    if (initialLoading) {
      return <Box sx={{ mt: 1 }}>{[1, 2, 3].map(i => <SkeletonCard key={i} />)}</Box>;
    }

    if (activeTab === 'buy' && showSkeleton) {
      return (
        <Box>
          {isTyping && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 1, py: 1.5 }}>
              <CircularProgress size={16} sx={{ color: '#8B7355' }} />
              <Typography variant="caption" sx={{ color: '#888' }}>Searching...</Typography>
            </Box>
          )}
          <Box sx={{ mt: isTyping ? 0.5 : 1 }}>
            {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </Box>
        </Box>
      );
    }

    if (properties.length === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center',
          alignItems: 'center', height: 200, gap: 1, px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'accessed'
              ? "You haven't accessed any properties yet"
              : selectedCategory
              ? `No properties found for "${selectedCategory.category}"`
              : debouncedSearch
              ? `No results for "${debouncedSearch}"`
              : 'No hot properties available right now.'}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {properties.map(renderCard)}
        <ScrollSentinel onVisible={loadMore} hasMore={hasMore} loading={listLoading} />
      </>
    );
  }, [
    initialLoading, activeTab, showSkeleton, isTyping, properties, 
    selectedCategory, debouncedSearch, renderCard, loadMore, 
    hasMore, listLoading
  ]);

  // ── JSX ──────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: 'rgb(239, 231, 221)', minHeight: '120vh' }}>

      {/* Fixed header */}
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
        backgroundColor: 'rgb(239, 231, 221)' }}>

        <Box sx={{ px: 1, py: 1 }}>
          <CustomSearchBar
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onPriceFilterChange={setPriceFilter}
          />
        </Box>

        {/* Tabs */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              if (newValue === 'accessed') setSelectedCategory(null);
            }}
            sx={{ '& .MuiTabs-indicator': { backgroundColor: 'black', height: 3 } }}
          >
            <Tab
              label="Hot Properties"
              value="buy"
              sx={{ textTransform: 'none', fontWeight: 'bold',
                color: activeTab === 'buy' ? 'black' : 'text.secondary', minWidth: 0, mr: 2 }}
            />
            <Tab
              label="Accessed Properties"
              value="accessed"
              sx={{ textTransform: 'none', fontWeight: 'bold',
                color: activeTab === 'accessed' ? 'black' : 'text.secondary', minWidth: 0 }}
            />
          </Tabs>
        </Box>

        {/* Category chips — only on Buy tab */}
        {activeTab === 'buy' && (
          <Box sx={{ px: 2, pb: 1 }}>
            {categoriesLoading ? (
              <ChipRowSkeleton />
            ) : (
              <Box sx={{
                display: 'flex', gap: 1, overflowX: 'auto', whiteSpace: 'nowrap', pb: 0.5,
                '&::-webkit-scrollbar': { height: 3 },
                '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: 2 },
              }}>
                {categories.map(cat => (
                  <Chip
                    key={cat.category_id}
                    label={cat.category}
                    variant="filled"
                    onClick={() =>
                      setSelectedCategory(prev =>
                        prev?.category_id === cat.category_id ? null : cat
                      )
                    }
                    sx={{
                      flexShrink: 0,
                      bgcolor:    selectedCategory?.category_id === cat.category_id ? '#000000' : 'transparent',
                      color:      selectedCategory?.category_id === cat.category_id ? '#ffffff' : '#000000',
                      border:     '1px solid black',
                      fontWeight: 'bold',
                      cursor:     'pointer',
                      '&:hover':  {
                        bgcolor: selectedCategory?.category_id === cat.category_id
                          ? '#000000' : 'rgba(0,0,0,0.1)',
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Scrollable list */}
      <Box sx={{
        pb: 8,
        pt: activeTab === 'buy'
          ? { xs: '175px', sm: '180px' }
          : { xs: '130px', sm: '115px' },
      }}>
        {renderPropertiesSection}
      </Box>

      {/* Modals */}
      <GuestLoginModal open={showGuestModal} onClose={() => setShowGuestModal(false)} />

      <AlertModal
        open={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        onConfirm={async () => { setShowAlertModal(false); if (pendingAction) await pendingAction(); }}
        title="Confirm Action"
        message="Are you sure you want to perform this action on this property?"
      />

      <SubscriptionModal
        open={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
        onSubscribe={() => { setShowSubscribeModal(false); navigate('/user-subscription'); }}
        featureRequested={featureRequested}
      />

      <LimitModal open={showLimitModal} onClose={() => setShowLimitModal(false)} userPlan={userPlan} />

      {showSuccess && (
        <CopySuccessAlert
          message={alertMessage}
          onClose={() => { setShowSuccess(false); setAlertMessage(''); }}
        />
      )}
      {showFailure && (
        <CopyFailureAlert
          message={alertMessage}
          onClose={() => { setShowFailure(false); setAlertMessage(''); }}
        />
      )}

      <BottomNavbar />
    </Box>
  );
};

export default PropertyCard;